import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Paper, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ForumOutlined from "@material-ui/icons/ForumOutlined";
import TicketsManagerTabs from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";
import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { toRem, logicalPixels } from "../../design/density";

const minWidth = 360;
const maxWidth = 620;
const clampWidth = (width) => Math.max(minWidth, Math.min(maxWidth, Number(width) || 420));
const useStyles = makeStyles((theme) => ({
  chatContainer: { height: "calc(100% - 64px)", padding: 16, overflow: "hidden" },
  chatPaper: { display: "flex", height: "100%", minWidth: 0, borderRadius: 12, overflow: "hidden", border: `1px solid ${theme.palette.divider}` },
  contacts: { display: "flex", height: "100%", flexDirection: "column", position: "relative", flexShrink: 0 },
  messages: { display: "flex", height: "100%", flexDirection: "column", flex: 1, minWidth: 0 },
  welcome: { background: theme.palette.background.default, display: "flex", justifyContent: "center", alignItems: "center", height: "100%", textAlign: "center", border: 0 },
  dragger: { position: "absolute", width: 6, cursor: "ew-resize", top: 0, right: -3, bottom: 0, zIndex: 10, background: "transparent", "&:hover, &:focus-visible": { background: theme.palette.primary.main } },
}));

const TicketsCustom = () => {
  const { user } = useContext(AuthContext);
  const { ticketId } = useParams();
  const classes = useStyles();
  const [width, setWidth] = useState(clampWidth(user?.defaultTicketsManagerWidth));
  const contactsRef = useRef(null);
  const containerRef = useRef(null);
  const widthRef = useRef(width);
  const cleanupRef = useRef(() => {});

  useEffect(() => {
    const next = clampWidth(user?.defaultTicketsManagerWidth);
    widthRef.current = next;
    setWidth(next);
  }, [user?.defaultTicketsManagerWidth]);
  useEffect(() => () => cleanupRef.current(), []);

  const saveWidth = useCallback(async (next) => {
    try { await api.put(`/users/toggleChangeWidht/${user.id}`, { defaultTicketsManagerWidth: next }); }
    catch (error) { console.error("Erro ao salvar largura da central", error); }
  }, [user.id]);

  const updateWidth = (next) => {
    const available = containerRef.current ? logicalPixels(containerRef.current.clientWidth, containerRef.current) : 1000;
    const bounded = Math.min(clampWidth(next), Math.max(minWidth, available - 320));
    widthRef.current = bounded;
    setWidth(bounded);
  };
  const startResize = (event) => {
    event.preventDefault();
    cleanupRef.current();
    const start = widthRef.current;
    const left = contactsRef.current.getBoundingClientRect().left;
    const onMove = (moveEvent) => updateWidth(logicalPixels(moveEvent.clientX - left, contactsRef.current));
    const cleanup = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      cleanupRef.current = () => {};
    };
    const onEnd = () => { cleanup(); if (widthRef.current !== start) saveWidth(widthRef.current); };
    cleanupRef.current = cleanup;
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
  };
  const resizeWithKeyboard = (event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault();
    updateWidth(widthRef.current + (event.key === "ArrowRight" ? 20 : -20));
    saveWidth(widthRef.current);
  };

  return <QueueSelectedProvider>
    <div className={classes.chatContainer}>
      <div className={classes.chatPaper} ref={containerRef}>
        <div ref={contactsRef} className={classes.contacts} style={{ width: toRem(width), maxWidth: "55%" }}>
          <TicketsManagerTabs />
          <div className={classes.dragger} role="separator" tabIndex={0} aria-label="Largura da lista de atendimentos" aria-orientation="vertical" aria-valuemin={minWidth} aria-valuemax={maxWidth} aria-valuenow={width} onMouseDown={startResize} onKeyDown={resizeWithKeyboard} />
        </div>
        <div className={classes.messages}>
          {ticketId ? <Ticket /> : <Paper square className={classes.welcome} elevation={0}>
            <div className="workspace-empty">
              <div className="workspace-empty-symbol"><ForumOutlined style={{ fontSize: toRem(36) }} /></div>
              <span className="eyebrow">CENTRAL DE RELACIONAMENTO</span>
              <h2>Cada conversa, uma oportunidade.</h2>
              <p>Selecione um atendimento para acessar o histórico, colaborar com sua equipe e dar continuidade à conversa.</p>
              <Button variant="outlined" color="primary" onClick={() => contactsRef.current?.querySelector("input")?.focus()}>Buscar atendimento</Button>
              <div className="workspace-empty-hint">Tudo o que você precisa para atender com contexto.</div>
            </div>
          </Paper>}
        </div>
      </div>
    </div>
  </QueueSelectedProvider>;
};
export default TicketsCustom;
